import { ArrowRight, Loader2, Search } from 'lucide-react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import { getDocumentIcon } from '@/lib/document-icons';

export interface FilenameSuggestion {
  documentGuid: string;
  fileReferenceGuid: string;
  filename: string;
}

export interface FilenameSuggestionPage {
  suggestions: FilenameSuggestion[];
  nextOffset: number | null;
}

interface DataTableSearchInputProps {
  placeholder?: string;
  isSearching: boolean;
  onSubmit: (query: string) => void;
  loadSuggestions?: (query: string, offset: number) => Promise<FilenameSuggestionPage>;
  onSuggestionSelect?: (suggestion: FilenameSuggestion) => void;
}

interface FilenameSuggestionOptionProps {
  suggestion: FilenameSuggestion;
  index: number;
  isHighlighted: boolean;
  onHighlight: (index: number) => void;
  onSelect: (suggestion: FilenameSuggestion) => void;
}

interface QuerySearchOptionProps {
  query: string;
  isHighlighted: boolean;
  onHighlight: (index: number) => void;
  onSubmit: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const preventOptionBlur = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault();

interface SearchTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  isSearching: boolean;
  onSubmitClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function SearchTextarea({ isSearching, onSubmitClick, ...props }: SearchTextareaProps) {
  const handleInput = useCallback((event: React.FormEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  }, []);

  return (
    <div className="flex w-full flex-row items-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs transition-shadow duration-300 ease-in-out focus-within:border-crimson focus-within:shadow-md">
      <div className="flex flex-1 flex-col">
        <textarea
          className="max-h-[150px] min-h-0 w-full resize-none overflow-y-auto bg-transparent px-3 py-1.5 text-sm leading-5 text-slate-800 placeholder:text-slate-400 focus:border-transparent focus:outline-hidden focus:ring-0"
          rows={1}
          onInput={handleInput}
          {...props}
        />
      </div>

      <div className="flex items-center px-2 py-1">
        <Button
          type="button"
          className="flex h-5 min-h-0 w-5 min-w-0 items-center justify-center rounded-full bg-slate-100 p-0 text-slate-600 hover:bg-slate-200 [&>svg]:h-2.5 [&>svg]:w-2.5"
          onClick={onSubmitClick}
          disabled={isSearching}
        >
          {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function QuerySearchOption({
  query,
  isHighlighted,
  onHighlight,
  onSubmit,
}: QuerySearchOptionProps) {
  const handleMouseEnter = useCallback(() => onHighlight(0), [onHighlight]);

  return (
    <button
      type="button"
      role="option"
      data-option-index={0}
      aria-selected={isHighlighted}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left ${
        isHighlighted ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-50'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseDown={preventOptionBlur}
      onClick={onSubmit}
    >
      <Search className="h-4 w-4 shrink-0 text-crimson" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">Search documents for “{query}”</span>
        <span className="block text-xs text-slate-500">Search document contents</span>
      </span>
    </button>
  );
}

function FilenameSuggestionOption({
  suggestion,
  index,
  isHighlighted,
  onHighlight,
  onSelect,
}: FilenameSuggestionOptionProps) {
  const handleMouseEnter = useCallback(() => onHighlight(index), [index, onHighlight]);
  const handleClick = useCallback(() => onSelect(suggestion), [onSelect, suggestion]);
  const { Icon, colorClass } = getDocumentIcon(suggestion.filename);

  return (
    <button
      type="button"
      role="option"
      data-option-index={index}
      aria-selected={isHighlighted}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm ${
        isHighlighted ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-50'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseDown={preventOptionBlur}
      onClick={handleClick}
    >
      <Icon className={`h-4 w-4 shrink-0 ${colorClass}`} />
      <span className="truncate">{suggestion.filename}</span>
    </button>
  );
}

export const DataTableSearchInput = React.memo(function DataTableSearchInput({
  placeholder,
  isSearching,
  onSubmit,
  loadSuggestions,
  onSuggestionSelect,
}: DataTableSearchInputProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<FilenameSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isLoadingMoreSuggestions, setIsLoadingMoreSuggestions] = useState(false);
  const [nextSuggestionsOffset, setNextSuggestionsOffset] = useState<number | null>(null);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const suggestionsListRef = useRef<HTMLDivElement>(null);
  const activeSuggestionsQueryRef = useRef('');
  const activeSuggestionsLoaderRef = useRef(loadSuggestions);
  const isLoadingMoreSuggestionsRef = useRef(false);
  const suggestionsContinuationRef = useRef<{
    query: string;
    loader: NonNullable<DataTableSearchInputProps['loadSuggestions']>;
    offset: number;
  } | null>(null);
  const suggestionsId = useId();

  activeSuggestionsLoaderRef.current = loadSuggestions;

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsSuggestionsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!loadSuggestions || normalizedQuery.length < 2) {
      activeSuggestionsQueryRef.current = '';
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      setIsLoadingMoreSuggestions(false);
      isLoadingMoreSuggestionsRef.current = false;
      setNextSuggestionsOffset(null);
      suggestionsContinuationRef.current = null;
      setIsSuggestionsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    let isCurrentRequest = true;
    activeSuggestionsQueryRef.current = normalizedQuery;
    setSuggestions([]);
    setIsLoadingSuggestions(true);
    setIsLoadingMoreSuggestions(false);
    isLoadingMoreSuggestionsRef.current = false;
    setNextSuggestionsOffset(null);
    suggestionsContinuationRef.current = null;
    setIsSuggestionsOpen(true);
    setHighlightedIndex(-1);

    const timeout = window.setTimeout(async () => {
      try {
        const page = await loadSuggestions(normalizedQuery, 0);
        if (!isCurrentRequest) return;
        setSuggestions(page.suggestions);
        setNextSuggestionsOffset(page.nextOffset);
        suggestionsContinuationRef.current = page.nextOffset === null
          ? null
          : {
              query: normalizedQuery,
              loader: loadSuggestions,
              offset: page.nextOffset,
            };
      } catch {
        if (!isCurrentRequest) return;
        setSuggestions([]);
        setNextSuggestionsOffset(null);
        suggestionsContinuationRef.current = null;
      } finally {
        if (isCurrentRequest) setIsLoadingSuggestions(false);
      }
    }, 250);

    return () => {
      isCurrentRequest = false;
      window.clearTimeout(timeout);
    };
  }, [loadSuggestions, query]);

  const loadMoreSuggestions = useCallback(async () => {
    const normalizedQuery = query.trim();
    const continuation = suggestionsContinuationRef.current;
    if (
      !loadSuggestions
      || !continuation
      || continuation.query !== normalizedQuery
      || continuation.loader !== loadSuggestions
      || isLoadingSuggestions
      || isLoadingMoreSuggestionsRef.current
    ) {
      return;
    }

    isLoadingMoreSuggestionsRef.current = true;
    setIsLoadingMoreSuggestions(true);
    try {
      const page = await loadSuggestions(normalizedQuery, continuation.offset);
      if (
        activeSuggestionsQueryRef.current !== normalizedQuery
        || activeSuggestionsLoaderRef.current !== loadSuggestions
      ) {
        return;
      }

      setSuggestions((currentSuggestions) => {
        const existingIds = new Set(
          currentSuggestions.map((suggestion) => suggestion.fileReferenceGuid),
        );
        return [
          ...currentSuggestions,
          ...page.suggestions.filter(
            (suggestion) => !existingIds.has(suggestion.fileReferenceGuid),
          ),
        ];
      });
      setNextSuggestionsOffset(page.nextOffset);
      suggestionsContinuationRef.current = page.nextOffset === null
        ? null
        : {
            query: normalizedQuery,
            loader: loadSuggestions,
            offset: page.nextOffset,
          };
    } catch {
      // Keep the continuation offset so another scroll can retry a transient failure.
      return;
    } finally {
      if (
        activeSuggestionsQueryRef.current === normalizedQuery
        && activeSuggestionsLoaderRef.current === loadSuggestions
      ) {
        isLoadingMoreSuggestionsRef.current = false;
        setIsLoadingMoreSuggestions(false);
      }
    }
  }, [
    isLoadingSuggestions,
    loadSuggestions,
    query,
  ]);

  const handleSuggestionsScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    const isNearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 32;
    if (isNearBottom) void loadMoreSuggestions();
  }, [loadMoreSuggestions]);

  useEffect(() => {
    if (!isSuggestionsOpen || highlightedIndex < 0) return;

    const highlightedOption = suggestionsListRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${highlightedIndex}"]`,
    );
    highlightedOption?.scrollIntoView?.({ block: 'nearest' });

  }, [
    highlightedIndex,
    isSuggestionsOpen,
  ]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    activeSuggestionsQueryRef.current = event.target.value.trim();
    suggestionsContinuationRef.current = null;
    setQuery(event.target.value);
  };

  const handleFocus = () => {
    if (query.trim().length >= 2) setIsSuggestionsOpen(true);
  };

  const selectSuggestion = (suggestion: FilenameSuggestion) => {
    setIsSuggestionsOpen(false);
    setHighlightedIndex(-1);
    onSuggestionSelect?.(suggestion);
  };

  const submitQuery = async (
    event: React.MouseEvent<HTMLInputElement | HTMLButtonElement>
      | React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    event.preventDefault();
    if (query === '') return;
    setIsSuggestionsOpen(false);
    setHighlightedIndex(-1);
    onSubmit(query);
  };

  const handleKeyUp = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      if (isSuggestionsOpen && highlightedIndex >= 0) {
        if (highlightedIndex === 0) {
          await submitQuery(event);
          return;
        }
        const suggestion = suggestions[highlightedIndex - 1];
        if (suggestion) selectSuggestion(suggestion);
        return;
      }
      await submitQuery(event);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
    }

    if (!isSuggestionsOpen) {
      if (event.key === 'Escape') setIsSuggestionsOpen(false);
      return;
    }

    const optionCount = (isLoadingSuggestions ? 0 : suggestions.length) + 1;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = (highlightedIndex + 1) % optionCount;
      setHighlightedIndex(nextIndex);
      if (nextIndex === suggestions.length && nextSuggestionsOffset !== null) {
        void loadMoreSuggestions();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const nextIndex = highlightedIndex <= 0 ? optionCount - 1 : highlightedIndex - 1;
      setHighlightedIndex(nextIndex);
      if (nextIndex === suggestions.length && nextSuggestionsOffset !== null) {
        void loadMoreSuggestions();
      }
    } else if (event.key === 'Escape') {
      setIsSuggestionsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const showSuggestions = Boolean(isSuggestionsOpen && query.trim().length >= 2);

  return (
    <div ref={containerRef} className="relative">
      <SearchTextarea
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onSubmitClick={submitQuery}
        isSearching={isSearching}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={suggestionsId}
      />

      {showSuggestions && (
        <div
          ref={suggestionsListRef}
          id={suggestionsId}
          role="listbox"
          aria-busy={isLoadingSuggestions || isLoadingMoreSuggestions}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto overscroll-contain rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
          onScroll={handleSuggestionsScroll}
        >
          <QuerySearchOption
            query={query.trim()}
            isHighlighted={highlightedIndex === 0}
            onHighlight={setHighlightedIndex}
            onSubmit={submitQuery}
          />
          <div className="my-1 border-t border-slate-100" />
          {isLoadingSuggestions ? (
            <div className="flex items-center justify-center p-3">
              <Spinner size="sm" />
            </div>
          ) : suggestions.length > 0 ? (
            <>
              {suggestions.map((suggestion, index) => (
                <FilenameSuggestionOption
                  key={suggestion.fileReferenceGuid}
                  suggestion={suggestion}
                  index={index + 1}
                  isHighlighted={highlightedIndex === index + 1}
                  onHighlight={setHighlightedIndex}
                  onSelect={selectSuggestion}
                />
              ))}
              {isLoadingMoreSuggestions ? (
                <div className="flex items-center justify-center p-3">
                  <Spinner size="sm" />
                </div>
              ) : null}
            </>
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">No matching filenames</div>
          )}
        </div>
      )}
    </div>
  );
});
